import React, { createContext, useState, useEffect, useContext } from 'react';
import { defaultProfile, Profile } from '../api/models';
import { getQueryParam } from '../utils/utils';
import { getProfile } from '../api/api';
import { themes,applyTheme } from '../themeManager';


interface ThemeContextProps {
    theme: string;
    switchTheme: (themeName: string, primaryColor: string) => void;
    setProfile: (profile: Profile) => void;
    isLoading: boolean;
    profile: Profile;
}


const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [theme, setTheme] = useState('default');
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [profile, setProfile] = useState<Profile>(defaultProfile);
    // const primaryColor = getQueryParam('primaryColor') || "#4B286D"; // Default primary color if not found
    const profileId = getQueryParam('profileId'); // Default profileId if not found
    const lang = getQueryParam('lang') || "en-ca";


    const fetchProfileDetails = async (profileId: string, lang: string) => {
     
        try {
            const customProfile = await getProfile(profileId, lang);
            setProfile(customProfile);
            switchTheme(theme, customProfile.primaryColor);
            window.document.title = customProfile.displayName;
            
        } catch (error) {
            console.error('Error fetching profile:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const applyTheme = (primaryColor: string) => {
        const rgb = hexToRgb(primaryColor);
        const lightenColor = lightenHexColorForFeedback(primaryColor, 70);
         // Ensure primaryColor is prefixed with #
         const formattedColor = primaryColor.startsWith('#') ? primaryColor : `#${primaryColor}`;
         window.document.documentElement.style.setProperty('--primary-color', formattedColor);
         window.document.documentElement.style.setProperty('--primary-color-rgb', rgb.join(', '));
         window.document.documentElement.style.setProperty('--active-background-color', lightenColor);
    };
    const switchTheme = (themeName: string, primaryColor: string) => {
        // Apply the updated theme
        applyTheme(primaryColor);
        setTheme(themeName);
    };

    const hexToRgb = (hex: string) => {
        const bigint = parseInt(hex.slice(1), 16);
        return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
    };

    const lightenHexColorForFeedback = (hex: string, percent: number) => {
        let r = parseInt(hex.substring(1, 3), 16);
        let g = parseInt(hex.substring(3, 5), 16);
        let b = parseInt(hex.substring(5, 7), 16);
      
        r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
        g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
        b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));
      
        return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b
          .toString(16)
          .padStart(2, "0")}`;
    }

    useEffect(() => {
        if (typeof window !== 'undefined' && profileId) {
            fetchProfileDetails(profileId, lang);
        }
    }, [profileId]);


    return (
        <ThemeContext.Provider value={{ theme, switchTheme,setProfile, isLoading, profile }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useProfileTheme = () => useContext(ThemeContext);
